(ns frontend.view.task.webentry-resolution.deal
  (:require
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.constraints :as constraints]
   [common.schema.option-filters :as option-filters]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.components.drag-layout :as drag-layout]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.components.simple-editor :as simple-editor]
   [frontend.view.task.webentry-resolution.ai-components :as ai-components]
   [frontend.view.task.webentry-resolution.components :as components]
   [frontend.view.task.webentry-resolution.events :as events]
   [potpuri.core :refer [fn-> fn->>]]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.util :as uu]))

(defn deal-associations-view
  [_signals deal-id associations]
  (let [deal (get @session/entities deal-id)
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        status (:oppo/status deal)
        passed-on (:oppo/passed-on deal)
        put-on-hold-on (:oppo/put-on-hold-on deal)
        target-asset-id (:oppo/target-asset deal)
        target-asset (get @session/entities target-asset-id)]
    [:div
     {:class "mb-4 rounded border border-solid border-base-light-border-paler"}
     [:div
      {:class "m-0 flex border-b border-solid border-base-light-border-paler text-sm"}
      [:div
       {:class "w-1/4 p-2 text-base-light-accent-text cursor-pointer"}
       [:span
        {:on-click (fn [_]
                     (simple-editor/open-editor deal))}
        (entity/display-string deal)]]
      [:div
       {:class "w-1/4 p-2"}
       [:span
        {:on-click (fn [_]
                     (simple-editor/open-editor target-asset))}
        (entity/display-string target-asset)]]
      [:div
       {:class "w-1/4 p-2"}
       (fu/display-data tenant-schema
                        tenant
                        :oppo/stage
                        (registry/get-field-schema tenant-schema :oppo/stage)
                        deal)]
      [:div
       {:class "w-1/4 p-2"}
       (cond
         (and (= status :oppo.status/passed)
              passed-on)
         (loc/tr :passed-on [(fu/display-date-timestamp passed-on)])
         (and (= status :oppo.status/on-hold)
              put-on-hold-on)
         (loc/tr :put-on-hold-on [(fu/display-date-timestamp put-on-hold-on)])
         :else
         (fu/display-data tenant-schema
                          tenant
                          :oppo/status
                          (registry/get-field-schema tenant-schema :oppo/status)
                          deal))]]
     (doall
      (for [association associations
            :let [contact (fu/get-association-party association :entity.type/person)
                  other (fu/association-target contact association)]]
        ^{:key (str "association-" (:db/id association) "-" deal-id)}
        [:div
         {:class "m-0 p-2 text-base-light-text-paler text-sm"}
         (loc/tr :webentry-resolution-association-info
                 [(entity/display-string contact)
                  (if (= other deal)
                    (loc/tr :deal-lower-case)
                    (loc/tr :target-asset-lower-case))
                  (loc/tr (:entity-association/type association))])]))]))

(defn deal-associations-info
  [{:keys [resolved-deal] :as signals} _associations]
  (let [local-state (r/atom {})
        toggle-open! (fn [_] (swap! local-state update :open? not))]
    (fn [_ associations]
      (let [open? (:open? @local-state)
            deal-resolved? (boolean @resolved-deal)]
        [:div
         {:class "flex flex-col gap-3 mt-4"}
         [alert/info
          {}
          [:div
           [:div
            (if deal-resolved?
              (loc/tr :deal-contact-association-info-title)
              (loc/tr :deal-contact-association-attention-title))]
           (when-not deal-resolved?
             [:div
              (loc/tr :deal-contact-association-info)])]]
         (when open?
           [:div
            [:div
             {:class "flex"}
             [:div
              {:class "w-1/4 p-2 text-xs"}
              (loc/tr :deal-name)]
             [:div
              {:class "w-1/4 p-2 text-xs"}
              (loc/tr :target-asset)]
             [:div
              {:class "w-1/4 p-2 text-xs"}
              (loc/tr :deal-stage)]
             [:div
              {:class "w-1/4 p-2 text-xs"}
              (loc/tr :deal-status)]]
            (doall
             (for [[deal-id associations] associations]
               ^{:key (str "deal-associations-" deal-id)}
               [deal-associations-view signals deal-id associations]))])
         [:div
          [button/sm-secondary-hollow
           {:on-click toggle-open!}
           (if open?
             (loc/tr :close)
             (loc/tr :view-details))]]]))))

(defn deal-info-bar
  [{:keys [message attachments deal-associations-lookup] :as signals}]
  (let [message @message
        submitted-attachments @attachments
        associations @deal-associations-lookup]
    [:div
     {:class "p-4"}
     (when-not (string/blank? message)
       [components/message-info message])
     (when-not (empty? associations)
       [deal-associations-info signals associations])
     (when-not (empty? submitted-attachments)
       [components/attachments-section signals :submitted-attachments
        (loc/tr :submitted-attachments-n [(count submitted-attachments)]) submitted-attachments])]))

(defn deal-summary-view
  [{:keys [_resolved-deal] :as _signals}]
  (let [local-state (r/atom {:hide-info? false})
        hide-info! (fn [_]
                     (swap! local-state assoc :hide-info? true))]
    (fn [{:keys [resolved-deal resolved-deal-attachments] :as signals}]
      (let [resolved-deal @resolved-deal
            tenant @session/tenant
            tenant-schema @session/tenant-schema
            deal-id (:db/id resolved-deal)
            entity-type :entity.type/oppo
            entity-type-name (loc/tr entity-type)
            type-lc (->> entity-type-name
                         str
                         string/lower-case)
            deal-name (entity/display-string resolved-deal)
            info-title (loc/tr :resolved-entity-created-info-title [entity-type-name deal-name])
            info-text (loc/tr :resolved-entity-created-info [type-lc deal-name])
            schema (events/get-deal-summary-schema signals)
            attachments @resolved-deal-attachments]
        [:div
         (when-not (-> @local-state
                       :hide-info?)
           [alert/info
            {}
            [:h3
             {:class "font-bold"}
             info-title]
            [:span info-text]
            [:div
             {:class    "absolute top-2 right-2 text-xl cursor-pointer"
              :on-click hide-info!}
             [:i.fal.fa-times]]])
         [:table
          {:class "mt-4 w-full max-w-full border-collapse overflow-x-auto overflow-y-auto border border-solid border-base-light-border-paler"}
          [:thead
           [:tr
            [:th
             {:class    "py-2 px-3 w-[24%] bg-base-regular"
              :col-span "2"}
             (loc/tr :resolved-entity-info [entity-type-name])]]]
          [:tbody
           (doall
            (for [[attribute field-schema] schema
                  :let [label (attribute/attribute-label tenant-schema attribute)
                        value (fu/get-value tenant tenant-schema resolved-deal attribute)
                        display-value
                        (fu/display-data tenant-schema
                                         tenant
                                         attribute
                                         field-schema
                                         resolved-deal
                                         :show-currency?
                                         true)
                        data-type (:field/data-type field-schema)
                        text? (some #{data-type}
                                    [:data.type/text-simple
                                     :data.type/text-multi
                                     :data.type/url-multi
                                     :data.type/text-multiline
                                     :data.type/text-richtext])]
                  :when (not (cu/blank-value? value))]
              ^{:key (str "deal-" deal-id "-" attribute)}
              [:tr
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                label]
               [:td
                {:class "py-2 px-3 w-[24%] align-middle text-semibold border-b border-solid border-base-light-border-paler"}
                (if text?
                  [components/truncated-text display-value 80 false]
                  display-value)]]))]]
         (when-not (empty? attachments)
           [components/attachments-section signals :resolved-deal-attachments
            (loc/tr :all-attachments-found [type-lc]) attachments])]))))

(defn deal-resolved-view
  [{:keys [resolved-deal] :as signals}]
  (let [resolved-deal @resolved-deal
        no-deal? (or (nil? resolved-deal)
                     (:common/disabled? resolved-deal))
        entity-type-name (loc/tr :entity.type/oppo)
        type-lc (->> entity-type-name
                     str
                     string/lower-case)]
    (if no-deal?
      [:div
       [:div
        {:class "flex flexd-col gap-4 justify-center items-center"}
        [:img
         {:class "max-w-32"
          :src   "/img/Company-is-deleted.svg"}]]
       [:div
        {:class "text-center font-bold"}
        (loc/tr :entity-not-found-header [type-lc])]
       [:div
        {:class "m-0 text-center text-sm"}
        (loc/tr :entity-not-found-info [type-lc type-lc])]]
      [deal-summary-view signals])))

(defn existing-deals-view
  [{:keys [existing-deals completed? asset-type] :as _signals}]
  (let [completed? @completed?
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        asset-type @asset-type
        asset-type-name (loc/tr asset-type)
        type-lc (->> asset-type-name
                     str
                     string/lower-case)
        existing-deals @existing-deals]
    [:div
     {:class "border border-solid border-base-light-border-paler rounded"}
     [:div
      {:class "py-3 px-4 border-b border-solid border-base-light-border-paler text-bold text-base-light-text"}
      (loc/tr :existing-deals-for-asset-type [type-lc])]
     [:div
      {:class "flex flex-col gap-1 p-4"}
      [:div
       {:class "flex"}
       [:div
        {:class "flex w-11/12"}
        [:div
         {:class "m-0 w-1/5 py-1 px-3 text-sm font-semibold"}
         (loc/tr :deal-name)]
        [:div
         {:class "m-0 w-1/5 py-1 px-3 text-sm font-semibold"}
         (loc/tr :target-asset)]
        [:div
         {:class "m-0 w-1/5 py-1 px-3 text-sm font-semibold"}
         (loc/tr :deal-stage)]
        [:div
         {:class "m-0 w-1/5 py-1 px-3 text-sm font-semibold"}
         (loc/tr :deal-status)]
        [:div
         {:class "m-0 w-1/5 py-1 px-3 text-sm font-semibold"}
         (loc/tr :pipeline)]]]
      (doall
       (for [deal existing-deals
             :let [status (:oppo/status deal)
                   show-reactivate-button? (and (not completed?)
                                                (some #{status}
                                                      [:oppo.status/passed
                                                       :oppo.status/on-hold]))]]
         ^{:key (str "existing-deal-" (:db/id deal))}
         [:div
          {:class "flex"}
          [:div
           {:class "flex border border-solid border-base-light-border-paler rounded w-11/12"}
           [:div
            {:class "m-0 w-1/5 py-2 px-3 text-sm"}
            [:span
             {:class    "cursor-pointer text-base-light-text-accent"
              :on-click (fn [_]
                          (simple-editor/open-editor deal))}
             (entity/display-string deal)]]
           [:div
            {:class "m-0 w-1/5 py-2 px-3 text-sm"}
            [:a
             {:href     "#"
              :on-click (fn [_]
                          (simple-editor/open-editor (->> deal
                                                          :oppo/target-asset
                                                          (get @session/entities))))}
             (fu/display-data tenant-schema
                              tenant
                              :oppo/target-asset
                              (registry/get-field-schema tenant-schema :oppo/target-asset)
                              deal)]]
           [:div
            {:class "m-0 w-1/5 py-2 px-3 text-sm"}
            (fu/display-data tenant-schema
                             tenant
                             :oppo/stage
                             (registry/get-field-schema tenant-schema :oppo/stage)
                             deal)]
           [:div
            {:class "m-0 w-1/5 py-2 px-3 text-sm"}
            (fu/display-data tenant-schema
                             tenant
                             :oppo/status
                             (registry/get-field-schema tenant-schema :oppo/status)
                             deal)]
           [:div
            {:class "m-0 w-1/5 py-2 px-3 text-sm"}
            (fu/display-data tenant-schema
                             tenant
                             :oppo/pipeline
                             (registry/get-field-schema tenant-schema :oppo/pipeline)
                             deal)]]
          [:div
           {:class "m-0 py-2 px-3 text-sm"}
           (when show-reactivate-button?
             [button/sm-primary-solid
              {:class-name "ms-3"
               :on-click   (fn [_]
                             (events/reactivate! deal))}
              (loc/tr :reactivate)])]]))]]))

(defn validate-value!
  [draft-state live-updates-state error-state field-schema updated-attribute new-value]
  (let [field-constraints (when (= (:field/attribute field-schema) updated-attribute)
                            (:field/constraints field-schema))
        errors (constraints/validate field-constraints
                                     nil
                                     new-value
                                     @draft-state
                                     (partial session/unique-check
                                              false
                                              (partial routing/open-entity))
                                     loc/tr)]
    (if errors
      (swap! error-state assoc updated-attribute errors)
      (swap! error-state dissoc updated-attribute))
    (if (nil? new-value)
      (swap! live-updates-state dissoc updated-attribute)
      (swap! live-updates-state assoc updated-attribute new-value))
    errors))

(defn entity-options
  "Entities -> `{:value :label}` option maps for the new select inputs."
  [entities]
  (->> entities
       (mapv (fn [e]
               {:value (:db/id e)
                :label (entity/display-string e)}))))

(defn save-deal-field!
  "Writes one creator field to the draft. Selecting a pipeline resets the stage
   to that pipeline's first stage, and filling a currency-value seeds its
   currency-symbol sibling from the tenant default when still blank."
  [{:keys [draft-state field-schema mandatory-fields]}]
  (fn [attribute new-value]
    (let [old-value     (get @draft-state attribute)
          update-stage? (and (= attribute :oppo/pipeline)
                             (not= new-value old-value))]
      (swap! draft-state assoc attribute new-value)
      (when update-stage?
        (swap! draft-state assoc :oppo/stage (events/first-stage new-value)))
      (when-let [sa (:field/secondary-attribute field-schema)]
        (when (and (not= sa attribute)
                   (some #{sa} (into [] mandatory-fields))
                   (not (get @draft-state sa))
                   (= :data.type/currency-value (:field/data-type field-schema)))
          (when-let [default-currency (:tenant/default-currency @session/tenant)]
            (swap! draft-state assoc sa default-currency)))))))

(defn deal-creator-inputs
  [{:keys [state attachments deal-creator-schema] :as signals} _title]
  (let [tenant-schema @session/tenant-schema
        local-state (r/atom {:dropdown-open? false})
        draft-state (r/cursor state [:deal])
        error-state (r/cursor state [:deal-errors])
        live-updates-state (r/cursor state [:deal-live-updates])
        attachments-state (r/cursor state [:deal-attachments])
        schema @deal-creator-schema
        initial-deal (events/initial-deal-values signals)]
    (swap! state assoc
           :deal initial-deal
           :deal-attachments {:selected (->> @attachments
                                             (map :db/id)
                                             set)})
    (doseq [[k val] (select-keys initial-deal (map first schema))]
      (validate-value! draft-state
                       live-updates-state
                       error-state
                       (registry/get-field-schema tenant-schema k)
                       k
                       val))
    (fn [{:keys [state asset-type assets resolved-asset completed? deal-creator-schema
                 mandatory-deal-fields processing?]
          :as   signals} title]
      (let [completed? @completed?
            asset-type @asset-type
            attachments-write-access? (f-permissions/field-write-access?
                                       :common/attachments
                                       :entity.type/oppo
                                       ;;handling selected pipeline from draft state:
                                       (:oppo/pipeline @draft-state))
            attachments @attachments
            schema @deal-creator-schema
            mandatory-fields @mandatory-deal-fields
            entity-type-name (loc/tr :entity.type/oppo)
            type-lc (->> entity-type-name
                         str
                         string/lower-case)
            errors @error-state
            mandatory-blanks (->> mandatory-fields
                                  (filter (fn [field]
                                            (cu/blank-value? (get @draft-state field))))
                                  (remove (fn [field]
                                            (and (= field :oppo/target-asset)
                                                 (:db/id @resolved-asset)))))
            processing? @processing?
            button-disabled? (boolean (or processing?
                                          (seq errors)
                                          (->> mandatory-blanks
                                               not-empty)))
            dropdown-open? (and (:dropdown-open? @local-state)
                                (not button-disabled?))
            disabled-tooltip-content
            (->> errors
                 (map second)
                 (concat
                  (->>
                   mandatory-blanks
                   (map (fn [attribute]
                          (loc/tr :attribute-is-mandatory
                                  [(let [fs (registry/get-field-schema @session/tenant-schema
                                                                       attribute)]
                                     (loc/field-name attribute fs))])))))
                 (map (fn [text]
                        ^{:key text}
                        [:div text]))
                 doall)
            tooltip disabled-tooltip-content
            entity-template-lookup (->> schema
                                        (into {}))
            schema (->> schema
                        (remove (fn-> second :field/data-type (= :data.type/currency-symbol))))]
        [:div
         {:class "border border-solid border-base-light-border-paler rounded"}
         [:div
          {:class "py-3 px-4 border-b border-solid border-base-light-border-paler text-bold text-base-light-text"}
          (or title (loc/tr :submitted-information))]
         [:div
          {:class "flex flex-col gap-1 p-4"}
          (doall
           (for [[attribute field-schema] schema
                 :let [value (get @draft-state attribute)]]
             ^{:key (str "deal-creator-" attribute "-" (:oppo/pipeline @draft-state))}
             [components/creator-field
              {:attribute      attribute
               :field-schema   (cond-> field-schema
                                 ;; pipeline options are limited to oppo pipelines
                                 ;; that accept the submitted asset type
                                 (= attribute :oppo/pipeline)
                                 (assoc :field/option-filter-fn
                                        (option-filters/combine-and
                                         (partial option-filters/pipelines
                                                  :entity.type/oppo)
                                         (partial option-filters/asset-type-pipelines
                                                  :entity.type/oppo
                                                  asset-type))))
               :value          value
               :label          (or (->> field-schema
                                        :field/translations
                                        :en)
                                   (loc/tr attribute)
                                   (when-let [fs (registry/get-field-schema
                                                  @session/tenant-schema
                                                  attribute)]
                                     (loc/field-name attribute fs)))
               :mandatory?     (boolean (some #{attribute} mandatory-fields))
               :error          (get @error-state attribute)
               :containing-entity @draft-state
               :validate!      (partial validate-value!
                                        draft-state
                                        live-updates-state
                                        error-state
                                        field-schema)
               :save!          (save-deal-field! {:draft-state      draft-state
                                                  :field-schema     field-schema
                                                  :mandatory-fields mandatory-fields})
               :set-error!     (fn [attr err] (swap! error-state assoc attr err))
               :secondary-args (when-let [secondary-attribute
                                          (:field/secondary-attribute field-schema)]
                                 (let [sec-templ (get entity-template-lookup
                                                      secondary-attribute)]
                                   {:attribute secondary-attribute
                                    :value     (or (get @draft-state secondary-attribute)
                                                   (when (= (:field/data-type sec-templ)
                                                            :data.type/currency-symbol)
                                                     (:tenant/default-currency @session/tenant)))}))
               :override-args  (merge
                                (when (or completed?
                                          (not (some #{(:access/type field-schema)}
                                                     [:field.access/write :field.access/create])))
                                  {:read-only? true
                                   :disabled?  true})
                                (when (= attribute :oppo/target-asset)
                                  (if-let [target-asset-id (:db/id @resolved-asset)]
                                    {:editor-type :editor.type/select
                                     :options     (entity-options [@resolved-asset])
                                     :value       target-asset-id}
                                    {:editor-type :editor.type/select
                                     :options     (->> @assets
                                                       (sort-by :common/lower-case-str)
                                                       entity-options)})))}]))]
         (when (and (not completed?)
                    (seq attachments)
                    attachments-write-access?)
           [:div
            {:class "flex flex-col px-4 pb-4"}
            [:div
             {:class "m-0 text-base-light-text-paler text-sm"}
             (loc/tr :deal-resolve-select-attachments)]
            [:div
             [components/attachment-selector attachments-state attachments]]])
         (when-not completed?
           ;;manually created dropdown to get more control over functionality:
           [:div
            {:class-name "pl-3 pb-4 inline-block"}
            [button/sm-primary-solid
             {:disabled? button-disabled?
              ;; :tooltip   tooltip
              :on-click  (fn [_]
                           (when-not button-disabled?
                             (swap! local-state update :dropdown-open? not)))}
             [:span (loc/tr :work-on-this-deal)
              (if dropdown-open?
                [:i.fal.fa-angle-up.ml-2 {:aria-hidden true}]
                [:i.fal.fa-angle-down.ml-2 {:aria-hidden true}])]]
            (when dropdown-open?
              [:div
               {:class "p-3 bg-base-light border border-solid border-base-light-border-paler rounded"}
               [:div
                [button/secondary-open
                 {:on-click (fn [_]
                              (when-not button-disabled?
                                (events/create-deal! signals
                                                     draft-state
                                                     attachments-state
                                                     nil)))}
                 [:span
                  [:i.fal.fa-plus]
                  (loc/tr :create-new-entity [type-lc])]]]
               [:div
                [button/secondary-open
                 {:disabled? button-disabled?
                  :on-click  (fn [_]
                               (when-not button-disabled?
                                 (events/create-deal! signals
                                                      draft-state
                                                      attachments-state
                                                      :pass)))}
                 [:span
                  [:i.fal.fa-ban]
                  (loc/tr :pass-deal)]]]
               [:div
                [button/secondary-open
                 {:disabled? button-disabled?
                  :on-click  (fn [_]
                               (when-not button-disabled?
                                 (events/create-deal! signals
                                                      draft-state
                                                      attachments-state
                                                      :put-on-hold)))}
                 [:span
                  [:i.fal.fa-pause-circle]
                  (loc/tr :put-on-hold)]]]])])]))))

(defn deal-creator
  [{:keys [existing-deals] :as signals} creator-title]
  (let [existing-deals @existing-deals]
    [:div
     {:class "flex flex-col gap-4 p-4"}
     (when-not (empty? existing-deals)
       [existing-deals-view signals])
     [deal-creator-inputs signals creator-title]]))

(defn create-deal-view
  [signals]
  [:div
   {:class "border border-solid border-base-light-border-paler rounded"}
   [:div
    {:class "p-3 border-b border-solid border-base-light-border-paler text-semibold"}
    (loc/tr :deal)]
   [deal-creator signals]])

(defn no-deal-info-view
  [signals]
  [:div
   [deal-creator signals (loc/tr :no-webform-deal-create-new)]])

(defn main-deal-view
  [{:keys [resolved-deal-data submitted-deal has-ai-suggestions? ai-deals ai-status] :as signals}]
  (let [resolved-deal-data @resolved-deal-data
        submitted-deal @submitted-deal
        deal-id (:deal-id resolved-deal-data)
        has-ai? @has-ai-suggestions?
        ai-deal-count (count @ai-deals)]
    [:div
     {:class "container my-4"}
     [:div
      {:class "flex justify-center"}
      [components/state-indicator signals]]
     [:div
      {:class "container mt-4 mx-auto max-w-[1024px]"}
      [ai-components/ai-unavailable-banner {:status @ai-status}]]
     ;; Show AI suggestions banner if available
     (when (and has-ai? (pos? ai-deal-count) (not deal-id))
       [:div
        {:class "container mt-4 mx-auto max-w-[1024px]"}
        [ai-components/ai-suggestions-banner
         {:deal-count ai-deal-count}]])
     [:div
      {:class "container mt-4 mx-auto max-w-[1024px]"}
      (cond
        (not (session/dealflow?))
        [:div
         {:class "text-center text-bold"}
         (loc/tr :no-access-to-dealflow)]
        deal-id
        [deal-resolved-view signals]
        (empty? submitted-deal)
        [no-deal-info-view signals]
        :else
        [create-deal-view signals])]]))

(defn view
  [drag-state {:keys [deal-info-bar? deal-associations-lookup] :as signals}]
  (let [top-bottom? false
        info? @deal-info-bar?
        common-contacts? (seq @deal-associations-lookup)]
    (if info?
      [(if top-bottom?
         drag-layout/top-bottom
         drag-layout/left-right)
       drag-state
       (uu/cm "webentry-resolution-layout"
              (when common-contacts?
                "common-contacts"))
       [deal-info-bar signals]
       [main-deal-view signals]]
      [main-deal-view signals])))
